import {
  Avatar,
  Badge,
  Box,
  Drawer,
  Grid,
  IconButton,
  SwipeableDrawer,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useTheme,
} from '@mui/material';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MenuIcon from '@mui/icons-material/Menu';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { isEmpty } from 'lodash';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthInfo, useLogoutFunction } from '@propelauth/react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import Typography from '@mui/material/Typography';

import { UseAuthPermissions } from '../../session/UseAuthPermissions';
import { GROW_CONNECT_FEATURE_FLAG } from '../../growConnect/growConnectUtils';
import 'moment/dist/locale/he';
import { NavigationItemButton } from './NavigationItemButton';
import { NavigationHeaderButton } from './NavigationHeaderButton';
import { useLanguage } from '../../common/GeneralUtils';
import { useAuth } from '../../session/InternalAuthProvider';
import { AccountData, Currency, HolidayCalendar } from '../../api';
import ConfirmationDialog from '../../common/ui/ConfimationDialog';
import { mb } from '../../common/theme/mbTokens';
import { SidebarIcon, SidebarIconName } from './sidebarIcons';
import { useOpenBalancesSummary } from '../../openBills/useOpenBalancesSummary';

export const mainSidebarWidth = 260;
/** The folded sidebar: one icon per menu entry, linked, with the name on hover. */
export const mainSidebarRailWidth = 56;
export type NavigationItem = {
  key: string;
  name: string;
  icon?: SidebarIconName;
  navigate: string;
  external?: boolean;
  target?: string;
  count?: number;
};

export type NavigationHeader = NavigationItem & {
  navigationSubItems?: (NavigationItem | undefined)[];
};
const MainSidebar = (props: any) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuthInfo();
  const { readOnly, isAtLeastSupervisor, isAdmin } = UseAuthPermissions();
  const { summary } = useOpenBalancesSummary();

  const logoutFn = useLogoutFunction();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const whatsappIntegrationDisabled = useFeatureFlagEnabled('feature-whatsapp-integration-disabled');
  const passesEnabled = useFeatureFlagEnabled('feature-passes') && !readOnly;
  const openBillsEnabled = useFeatureFlagEnabled('feature-open-bills') && !readOnly;
  const growConnectFlagEnabled = useFeatureFlagEnabled(GROW_CONNECT_FEATURE_FLAG);
  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const handleLogoutDialog = (confirmed: boolean) => {
    setShowLogoutDialog(false);
    if (confirmed) {
      void logoutFn(true);
    }
  };
  const titleCase = (title?: string) => {
    if (!title) {
      return '';
    }

    return title[0].toUpperCase() + title.slice(1).toLowerCase();
  };
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;

  const { isSidebarOpen, setIsSidebarOpen, isCollapsed, onCollapse, onExpand } = props;
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const language = useLanguage();
  const activityLabel = account?.activity_label_plural?.[language];

  const paymentsEnabled = account.currency === Currency.USD;
  const isUsAccount = account.currency === Currency.USD;
  // Grow is an Israeli clearing provider, and opening an account in the business's name asks
  // for the owner's ID and bank details -- so the option is absent, not disabled, for anyone else.
  const growConnectEnabled = isAdmin && !!growConnectFlagEnabled && account.holiday_calendar === HolidayCalendar.ISR;
  const navigations: (NavigationHeader | undefined)[] = [
    {
      key: 'calendar',
      name: t('main_sidebar.calendar'),
      icon: SidebarIconName.Calendar,
      navigate: '/dashboard/calendar',
    },
    !readOnly
      ? {
          key: 'orders',
          name: t('main_sidebar.orders'),
          icon: SidebarIconName.Bookings,
          navigate: '/dashboard/orders',
        }
      : undefined,
    openBillsEnabled
      ? {
          key: 'open-bills',
          name: t('main_sidebar.open_bills'),
          icon: SidebarIconName.PaymentLinks,
          navigate: '/dashboard/open-bills',
          count: summary?.count,
        }
      : undefined,
    passesEnabled
      ? {
          key: 'passes',
          name: t('main_sidebar.pass_orders'),
          icon: SidebarIconName.Memberships,
          navigate: '/dashboard/passes',
        }
      : undefined,
    !readOnly
      ? {
          key: 'vouchers',
          name: t('main_sidebar.vouchers'),
          icon: SidebarIconName.Vouchers,
          navigate: '/dashboard/vouchers',
        }
      : undefined,
    account.external_vouchers_enabled && !readOnly
      ? {
          key: 'externalVouchers',
          name: t('main_sidebar.externalVouchers'),
          icon: SidebarIconName.ExternalVouchers,
          navigate: '/dashboard/external-vouchers',
        }
      : undefined,

    !readOnly
      ? {
          key: 'paymentLinks',
          name: t('main_sidebar.paymentLinks'),
          icon: SidebarIconName.PaymentLinks,
          navigate: '/dashboard/payment-links',
        }
      : undefined,
    !readOnly
      ? {
          key: 'waitingList',
          name: t('main_sidebar.waitingList'),
          icon: SidebarIconName.Waitlists,
          navigate: '/dashboard/waiting-list',
        }
      : undefined,

    {
      key: 'order-page',
      name: t('main_sidebar.order_pages', { activityLabel }),
      icon: SidebarIconName.Workshops,
      navigate: '/dashboard/order-pages',
    },
    {
      key: 'discounts',
      name: t('main_sidebar.discounts'),
      icon: SidebarIconName.Discounts,
      navigate: `/dashboard/discounts`,
    },
    !readOnly
      ? {
          key: 'storefronts',
          name: t('main_sidebar.storefronts'),
          icon: SidebarIconName.MiniSites,
          navigate: `/dashboard/storefronts`,
        }
      : undefined,
    isAtLeastSupervisor
      ? {
          key: 'settings',
          name: t('main_sidebar.settings'),
          icon: SidebarIconName.SystemSettings,
          navigate: `/dashboard/account-settings`,
          navigationSubItems: [
            {
              key: 'account-settings',
              navigate: '/dashboard/account-settings',
              name: t('main_sidebar.settings'),
              icon: SidebarIconName.General,
            },
            {
              key: 'business-details',
              navigate: '/dashboard/business-details',
              name: t('main_sidebar.storefronts_business_details'),
              icon: SidebarIconName.Business,
            },
            {
              key: 'catalog',
              navigate: '/dashboard/catalog',
              name: t('main_sidebar.catalog'),
              icon: SidebarIconName.Catalog,
            },
            {
              key: 'email-templates',
              navigate: '/dashboard/email-templates',
              name: t('main_sidebar.email_templates'),
              icon: SidebarIconName.EmailTemplates,
            },
            account.currency === Currency.USD
              ? {
                  key: 'sms-templates',
                  navigate: '/dashboard/sms-templates',
                  name: t('main_sidebar.sms_templates'),
                }
              : undefined,
            !whatsappIntegrationDisabled
              ? {
                  key: 'whatsapp-settings',
                  navigate: '/dashboard/whatsapp-settings',
                  name: t('main_sidebar.whatsapp_settings'),
                  icon: SidebarIconName.Whatsapp,
                }
              : undefined,
            paymentsEnabled
              ? {
                  key: 'payments',
                  name: t('main_sidebar.payments'),
                  navigate: `/dashboard/payments`,
                }
              : undefined,
            growConnectEnabled
              ? {
                  key: 'grow-connect',
                  name: t('main_sidebar.grow_connect'),
                  navigate: `/dashboard/grow-connect`,
                }
              : undefined,
            account.svix_application_id
              ? {
                  key: 'svix-portal',
                  name: t('main_sidebar.svix_portal'),
                  icon: SidebarIconName.Webhooks,
                  navigate: `/dashboard/webhooks`,
                }
              : undefined,
            !readOnly
              ? {
                  key: 'qrBoards',
                  name: t('main_sidebar.qrBoards'),
                  icon: SidebarIconName.QrBoards,
                  navigate: '/dashboard/qr-boards',
                }
              : undefined,
            isAdmin
              ? {
                  key: 'employees',
                  name: t('main_sidebar.employees'),
                  icon: SidebarIconName.Users,
                  navigate: '/dashboard/users',
                }
              : undefined,
          ],
        }
      : undefined,
    {
      key: 'user-settings',
      name: t('main_sidebar.usersettings'),
      icon: SidebarIconName.UserSettings,
      navigate: '/dashboard/user-settings',
    },
    isAdmin
      ? {
          key: 'reports',
          name: t('main_sidebar.reports'),
          icon: SidebarIconName.Reports,
          navigate: `/dashboard/reports`,
        }
      : undefined,
    isAdmin
      ? {
          key: 'analytics',
          name: t('main_sidebar.analytics'),
          icon: SidebarIconName.SalesData,
          navigate: `/dashboard/analytics`,
        }
      : undefined,
    !readOnly
      ? {
          key: 'audit-log',
          name: t('main_sidebar.audit_log'),
          icon: SidebarIconName.ActivityLog,
          navigate: `/dashboard/audit-log`,
        }
      : undefined,
  ];

  const changeLang = (lang: string) => {
    void i18n.changeLanguage(lang);
    document.body.dir = i18n.dir();
    theme.direction = i18n.dir();
    moment.locale(lang);
    localStorage.setItem('calendar_language', lang);
  };
  // @ts-ignore
  // eslint-disable-next-line no-unsafe-optional-chaining
  const userName = user ? user?.firstName?.[0].toUpperCase() + user.lastName?.[0].toUpperCase() : '';
  const drawer = (
    <Grid
      container
      direction="column"
      flexWrap="nowrap"
      gap={2.5}
      sx={{
        height: '100%',
        width: mainSidebarWidth,
        boxSizing: 'border-box',
        p: '24px 16px 12px',
        bgcolor: 'mb.side',
        color: 'text.primary',
        borderInlineEnd: '1px solid',
        borderColor: 'mb.sideLine',
      }}>
      <Grid container direction="column" px={1}>
        <Grid container justifyContent="space-between" alignItems="center" flexWrap="nowrap" gap={1}>
          <Box
            width="150px"
            component="img"
            src="/images/logos/monkeybook-light.png"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/dashboard/calendar')}
          />
          {/* Desktop only: folds the permanent sidebar away; the phone drawer closes by swipe or backdrop. */}
          {onCollapse && (
            <IconButton
              aria-label={t('main_sidebar.collapse_menu')}
              size="small"
              onClick={onCollapse}
              sx={{
                display: { xs: 'none', md: 'inline-flex' },
                flexShrink: 0,
              }}>
              <MenuOpenIcon fontSize="small" />
            </IconButton>
          )}
        </Grid>
      </Grid>
      {/* The opposite dir puts the scrollbar on the screen's outer edge, away from the content; the list
          restores the real dir. An attribute, not CSS: the RTL style plugin would flip a CSS direction. */}
      <Grid
        item
        dir={i18n.dir() === 'rtl' ? 'ltr' : 'rtl'}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
          scrollbarColor: 'transparent transparent',
          '&:hover': { scrollbarColor: `${mb.scrollThumb} transparent` },
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'transparent', borderRadius: '3px' },
          '&:hover::-webkit-scrollbar-thumb': { backgroundColor: mb.scrollThumb },
        }}>
        <Grid container direction="column" flexWrap="nowrap" gap={0.25} dir={i18n.dir()}>
          {navigations.map((item) => {
            if (!item) return null;
            if (!isEmpty(item.navigationSubItems)) {
              return (
                <NavigationHeaderButton key={item.key} navigationHeader={item} setIsSidebarOpen={setIsSidebarOpen} />
              );
            }
            return <NavigationItemButton key={item.key} setIsSidebarOpen={setIsSidebarOpen} navigationItem={item} />;
          })}
        </Grid>
      </Grid>

      {user && (
        <Grid
          container
          alignItems="center"
          wrap="nowrap"
          gap={1}
          sx={{
            marginTop: 'auto',
            pt: 1.5,
            borderTop: '1px solid',
            borderColor: 'mb.sideLine',
          }}>
          <Avatar
            variant="rounded"
            sx={{
              width: 32,
              height: 32,
              flexShrink: 0,
              bgcolor: 'primary.main',
              color: 'primary.light',
              fontSize: 13,
              fontWeight: 600,
            }}>
            {userName}
          </Avatar>
          <Tooltip title={`${titleCase(user?.firstName)} ${titleCase(user?.lastName)}`}>
            <Typography noWrap sx={{ flex: 1, minWidth: 0, fontWeight: 500 }}>
              {titleCase(user?.firstName)} {titleCase(user?.lastName)}
            </Typography>
          </Tooltip>
          {!isUsAccount && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={language}
              aria-label={t('main_sidebar.language')}
              onChange={(_, value: string | null) => {
                if (value && value !== language) changeLang(value === 'en' ? 'en-US' : 'he');
              }}
              sx={{
                flexShrink: 0,
                '& .MuiToggleButton-root': {
                  minHeight: 30,
                  height: 30,
                  paddingInline: 1,
                  fontSize: 12,
                  fontWeight: 600,
                },
              }}>
              <ToggleButton value="he" lang="he" aria-label="עברית">
                עב
              </ToggleButton>
              <ToggleButton value="en" lang="en" aria-label="English">
                EN
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          <Tooltip title={t('top_bar.logout')}>
            <IconButton
              aria-label={t('top_bar.logout')}
              onClick={handleLogoutClick}
              sx={{ width: 36, height: 36, flexShrink: 0, color: 'text.secondary' }}>
              <SidebarIcon name={SidebarIconName.Logout} size={20} />
            </IconButton>
          </Tooltip>
        </Grid>
      )}
    </Grid>
  );

  const tooltipSide = i18n.dir() === 'rtl' ? 'left' : 'right';
  const rail = (
    <Grid
      container
      direction="column"
      alignItems="center"
      flexWrap="nowrap"
      gap={0.5}
      sx={{
        display: { xs: 'none', md: 'flex' },
        width: mainSidebarRailWidth,
        height: '100dvh',
        position: 'sticky',
        top: 0,
        paddingBlock: 1,
        bgcolor: 'mb.side',
        color: 'text.primary',
        borderInlineEnd: '1px solid',
        borderColor: 'mb.sideLine',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}>
      <IconButton aria-label={t('main_sidebar.expand_menu')} onClick={onExpand}>
        <MenuIcon />
      </IconButton>
      {navigations
        .filter((navigation): navigation is NavigationHeader => Boolean(navigation))
        .map((navigation) => {
          const isActive = pathname.startsWith(navigation.navigate);
          return (
            <Tooltip key={navigation.key} title={navigation.name} placement={tooltipSide}>
              <IconButton
                component={Link}
                to={navigation.navigate}
                aria-label={navigation.name}
                aria-current={isActive ? 'page' : undefined}
                sx={{
                  color: isActive ? 'common.white' : 'text.primary',
                  backgroundColor: isActive ? 'primary.main' : 'transparent',
                  '--mb-icon-wash': isActive ? mb.iconWashActive : undefined,
                  '&:hover': { backgroundColor: isActive ? 'primary.dark' : 'mb.ground' },
                  '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
                }}>
                <Badge badgeContent={navigation.count} color="error">
                  {navigation.icon && <SidebarIcon name={navigation.icon} size={22} />}
                </Badge>
              </IconButton>
            </Tooltip>
          );
        })}
      <Grid sx={{ flexGrow: 1 }} />
      <Tooltip title={t('top_bar.logout')} placement={tooltipSide}>
        <IconButton aria-label={t('top_bar.logout')} onClick={handleLogoutClick}>
          <SidebarIcon name={SidebarIconName.Logout} size={20} />
        </IconButton>
      </Tooltip>
    </Grid>
  );

  return (
    <>
      <SwipeableDrawer
        onOpen={() => setIsSidebarOpen(true)}
        anchor="left"
        variant="temporary"
        open={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', sm: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: mainSidebarWidth,
            bgcolor: 'mb.side',
          },
        }}>
        {drawer}
      </SwipeableDrawer>

      {!isCollapsed ? (
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'none', md: 'block' },
            '& .MuiPaper-root': {
              overflowX: 'hidden',
            },
            '& .MuiDrawer-paper': {
              border: 'none',
              bgcolor: 'mb.side',
              boxSizing: 'border-box',
              width: mainSidebarWidth,
              zIndex: 100,
            },
          }}
          open>
          {drawer}
        </Drawer>
      ) : (
        rail
      )}
      <ConfirmationDialog
        isOpen={showLogoutDialog}
        onClose={handleLogoutDialog}
        title={t('common.are_you_sure')}
        content={t('common.logout_confirmation')}
        ctaText={t('top_bar.logout')}
        confirmButtonColor="primary"
      />
    </>
  );
};

export default MainSidebar;
